import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import axios from 'axios';
import { AUTH_URL } from '../../constants/api';
import IUser from '../../types/user.type';

interface OAuthRedirectProps {
  updateUserState: (user: IUser) => void;
}

const OAuthRedirect: React.FC<OAuthRedirectProps> = ({ updateUserState }) => {
  const navigate = useNavigate();

  useEffect(() => {
    let listenerHandle: PluginListenerHandle | undefined;

    const processToken = (token: string) => {
      console.log("OAuth token received");

      axios
        .get(AUTH_URL + 'me', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
        .then(res => {
          console.log("✅ User received:", res.data);

          const data = res.data;

          const user: IUser = {
            id: data.id,
            username: data.username,
            email: data.email,
            roles: data.roles
          };

          // Zapisujemy token razem z użytkownikiem,
          // ale nie dokładamy go do typu IUser.
          localStorage.setItem(
            'user',
            JSON.stringify({
              ...user,
              accessToken: token
            })
          );

          updateUserState(user);

          const roles = user.roles ?? [];

          if (roles.includes('ROLE_USER')) {
            navigate('/training');
          } else if (roles.includes('ROLE_TRAINER')) {
            navigate('/programs');
          } else {
            navigate('/profile');
          }
        })
        .catch(err => {
          console.error("OAuth user request failed:", err);
          navigate('/login');
        });
    };

    const handleOAuthUrl = (url: string) => {
      console.log("📱 OAuth URL:", url);

      try {
        const parsedUrl = new URL(url);
        const token = parsedUrl.searchParams.get('token');

        console.log("Extracted token:", token);

        if (token) {
          processToken(token);
        } else {
          console.error("No token in OAuth callback");
          navigate('/login');
        }
      } catch (error) {
        console.error("Invalid OAuth callback URL:", error);
        navigate('/login');
      }
    };

    const initialize = async () => {

      // Android / Capacitor
      listenerHandle = await App.addListener(
        'appUrlOpen',
        (event: URLOpenListenerEvent) => {
          handleOAuthUrl(event.url);
        }
      );

      // WWW / HashRouter
      const hash = window.location.hash;

      const queryIndex = hash.indexOf('?');

      if (queryIndex !== -1) {
        const queryString = hash.substring(queryIndex + 1);
        const params = new URLSearchParams(queryString);
        const token = params.get('token');

        if (token) {
          console.log("🌐 Web OAuth token:", token);
          processToken(token);
        }
      }
    };

    initialize();

    return () => {
      listenerHandle?.remove();
    };
  }, [navigate, updateUserState]);

  return <div>Signing you in...</div>;
};

export default OAuthRedirect;